<template>
  <app-custom-dialog
    :value="value"
    title="compare_excel_with_data"
    width="90vw"
    dialog-height="85vh"
    :footer="true"
    :close-btn="true"
    :flex-layout="true"
    cancel-text="cancel"
    submit-text="confirm_import"
    :disable-submit="totalErrors > 0"
    :loading="confirming"
    @input="$emit('input', $event)"
    @submit="onConfirm"
  >
    <div slot="activator">
      <div v-if="summary" class="compare-summary">
        <div class="compare-summary-stat">
          <div class="compare-summary-value">{{ summary.main_total }}</div>
          <div class="compare-summary-label">{{ $t("ingredient") }}</div>
        </div>
        <div class="compare-summary-stat">
          <div class="compare-summary-value">{{ summary.sub_total }}</div>
          <div class="compare-summary-label">{{ $t("unit_conversion") }}</div>
        </div>
        <div
          class="compare-summary-stat compare-summary-stat--error"
          v-if="totalErrors > 0"
        >
          <div class="compare-summary-value">{{ totalErrors }}</div>
          <div class="compare-summary-label">{{ $t("error") }}</div>
        </div>
      </div>

      <v-tabs v-model="activeTab" grow class="mt-2">
        <v-tab>{{ $t("main_sheet") }}</v-tab>
        <v-tab>{{ $t("sub_sheet") }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-data-table
            :headers="mainHeaders"
            :items="mainRows"
            :items-per-page="15"
            disable-sort
            dense
            class="mt-3"
          >
            <template #[`item.ingredient_name`]="{ item }">
              <span>{{ item.ingredient_name }}</span>
            </template>
            <template #[`item.errors`]="{ item }">
              <v-chip
                v-for="(err, i) in item.errors"
                :key="i"
                x-small
                color="red"
                dark
                class="mr-1 mb-1"
              >
                {{ $t(err) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item>
          <v-data-table
            :headers="subHeaders"
            :items="subRows"
            :items-per-page="15"
            disable-sort
            dense
            class="mt-3"
          >
            <template #[`item.ingredient_name`]="{ item }">
              <span>{{ item.ingredient_name }}</span>
              <v-chip
                v-if="item.changed"
                x-small
                color="orange"
                dark
                class="ml-1"
              >
                {{ $t("changed") }}
              </v-chip>
            </template>
            <template #[`item.sort_order`]="{ item }">
              {{ formatNumber(item.sort_order) }}
            </template>
            <template #[`item.purpose`]="{ item }">
              {{ item.purpose === "restock" ? $t("restock") : $t("usage") }}
            </template>
            <template #[`item.from_quantity`]="{ item }">
              {{ formatNumber(item.from_quantity) }}
            </template>
            <template #[`item.to_quantity`]="{ item }">
              {{ formatNumber(item.to_quantity) }}
            </template>
            <template #[`item.errors`]="{ item }">
              <v-chip
                v-for="(err, i) in item.errors"
                :key="i"
                x-small
                color="red"
                dark
                class="mr-1 mb-1"
              >
                {{ $t(err) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </app-custom-dialog>
</template>

<script>
export default {
  name: "IngredientUnitConversionCompareDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    compareData: {
      type: Object,
      default: null,
    },
    // Parent-controlled: true while the real ingredient_unit_conversion_import
    // request is in flight, so the confirm button shows a spinner and can't
    // be double-clicked. The parent owns this because the actual API call
    // (and the file it re-uploads) lives in the parent, not this dialog.
    confirming: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    activeTab: 0,
  }),
  methods: {
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "";
      const num = Number(value);
      if (Number.isNaN(num)) return value;
      return num.toLocaleString();
    },
    onConfirm() {
      this.$emit("confirm", this.compareData);
    },
  },
  computed: {
    mainRows() {
      return this.compareData?.main ?? [];
    },
    subRows() {
      return this.compareData?.sub ?? [];
    },
    summary() {
      return this.compareData?.summary ?? null;
    },
    totalErrors() {
      if (!this.summary) return 0;
      return (this.summary.main_errors ?? 0) + (this.summary.sub_errors ?? 0);
    },
    mainHeaders() {
      return [
        { text: this.$t("ingredient"), value: "ingredient_name" },
        { text: this.$t("unit"), value: "unit_name" },
        { text: this.$t("error"), value: "errors", sortable: false },
      ];
    },
    subHeaders() {
      return [
        { text: this.$t("ingredient"), value: "ingredient_name" },
        { text: this.$t("sort_order"), value: "sort_order", align: "end" },
        { text: this.$t("purpose"), value: "purpose" },
        { text: this.$t("from_quantity"), value: "from_quantity", align: "end" },
        { text: this.$t("from_unit"), value: "from_unit_name" },
        { text: this.$t("to_quantity"), value: "to_quantity", align: "end" },
        { text: this.$t("to_unit"), value: "to_unit_name" },
        { text: this.$t("error"), value: "errors", sortable: false },
      ];
    },
  },
};
</script>

<style scoped>
.compare-summary {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.compare-summary-stat {
  flex: 1 1 100px;
  background: #f4f7fb;
  border-radius: 10px;
  padding: 10px 8px;
  text-align: center;
}
.compare-summary-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}
.compare-summary-label {
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
}
.compare-summary-stat--new {
  background: #e6f7ee;
  color: #0f5132;
}
.compare-summary-stat--changed {
  background: #fff4e5;
  color: #7a4a00;
}
.compare-summary-stat--error {
  background: #fdeceb;
  color: #842029;
}
::v-deep tbody td {
  font-size: 16px !important;
}
::v-deep .v-data-table--dense > .v-data-table__wrapper > table > tbody > tr > td,
::v-deep .v-data-table--dense > .v-data-table__wrapper > table > thead > tr > td,
::v-deep .v-data-table--dense > .v-data-table__wrapper > table > tfoot > tr > td {
  height: 38px;
}
::v-deep .v-chip .v-chip__content {
  align-items: center;
  display: inline-flex;
  height: 100%;
  max-width: 100%;
  font-size: 11px;
}
</style>
